import React, { useState } from "react";
import { Drawer, Form, Button, Select, Checkbox, Space, Card, Typography, Row, Col, message, Input, InputNumber } from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";

const { Title } = Typography;

/** ------------------ CONFIG ------------------ */
export const SECTION_LIST = [
  { id: 1, label: "Hump", options: [
    { code: "both_hump", label: "Both hump" },
    { code: "bony_hump", label: "Bony hump" },
    { code: "cartilage_hump", label: "Cartilage hump" },
    { code: "rasp", label: "Rasp" },
    { code: "none", label: "None" },
  ]},

   { id: 2, label: "S.D", options: [
      { code: "Septal_deviation", label: "Septal deviation", requiresSides: true },
  ]},
  { id: 3, label: "Nasal bone depression", options: [
    { code: "depression", label: "Depression", requiresSides: true },
    { code: "s_shape", label: "S-shape" },
  ]},
  { id: 4, label: "Septal depression", options: [
    { code: "depression", label: "Depression", requiresSides: true },
    { code: "s_shape", label: "S-shape" },
  ]},
  { id: 5, label: "Nasal obstruction", options: [
    { code: "left_obstruction",  label: "Left obstruction",  severityOptions: ["partial","complete"] },
    { code: "right_obstruction", label: "Right obstruction", severityOptions: ["partial","complete"] },
    { code: "both_obstruction",  label: "Both obstruction",  severityOptions: ["partial","complete"] },
  ]},
  { id: 6, label: "Grafts", options: [
    { code: "spreader_graft",           label: "Spreader graft", requiresSides: true },
    { code: "tension_suture",           label: "Tension suture to", requiresSides: true },
    { code: "l_shape_graft",            label: "L-shape graft", requiresSides: true },
    { code: "overlay_graft",            label: "Overlay graft", requiresSides: true },
    { code: "anterior_vertical_graft",  label: "Anterior vertical graft", requiresSides: true },
    { code: "columellar_strut_graft",   label: "Columellar strut graft" },
    { code: "alar_strut_graft",         label: "Alar strut graft", requiresSides: true },
    { code: "under_bony_graft",         label: "Under bony graft", requiresSides: true },
    { code: "supra_tip_graft",          label: "Supra tip graft" },
    { code: "cap_graft",                label: "Cap graft" },
    { code: "shield_graft",             label: "Shield graft", variants: [
      { code: "upper", label: "Upper" },
      { code: "lower", label: "Lower" },
      { code: "long",  label: "Long"  },
    ]},
    { code: "lateral_columellar_graft", label: "Lateral columellar graft", requiresSides: true },
    { code: "soft_triangle_graft",      label: "Soft triangle graft", requiresSides: true },
    { code: "camouflage_graft",         label: "Camouflage graft" },
    { code: "septal_extension_graft",   label: "Septal extension graft", requiresSides: true },
  ]},
  { id: 7, label: "S.M.D", options: [
    { code: "done", label: "Done" },
    { code: "none", label: "None" },
  ]},
  { id: 8, label: "Turbinectomy", options: [
    { code: "left_grade",  label: "Left (grade/amount)" },
    { code: "right_grade", label: "Right (grade/amount)" },
    { code: "bilateral_partial", label: "Bilateral partial turbinectomy" },
  ]},
  { id: 9, label: "Tip plasty", options: [
    { code: "broad",         label: "Broad" },
    { code: "very_broad",    label: "Very broad" },
    { code: "dropped",       label: "Dropped" },
    { code: "very_dropped",  label: "Very dropped" },
    { code: "deviated",      label: "Deviated", requiresSides: true },
    { code: "twisted",       label: "Twisted", requiresSides: true },
    { code: "columellar_show_wide",       label: "Columellar show (wide)", requiresSides: true },
    { code: "columellar_show_very_wide",  label: "Columellar show (very wide)", requiresSides: true },
    { code: "depressed_roof",             label: "Depressed roof", requiresSides: true },
  ]},
  { id: 10, label: "Nostrils", options: [
    { code: "wide",       label: "Wide", requiresSides: true },
    { code: "very_wide",  label: "Very wide", requiresSides: true },
    { code: "asymmetry",  label: "Asymmetry" },
    { code: "wide_base_columella", label: "Wide base columella" },
    { code: "alar_approximation_suture", label: "Alar approximation suture" },
  ]},
];

// Lookups
const SECTION_BY_ID = SECTION_LIST.reduce((acc, s) => { acc[s.id] = s; return acc; }, {});
const SECTION_OPTIONS = SECTION_LIST.map(s => ({ value: s.id, label: `${s.id} · ${s.label}` }));

const SIDE_OPTIONS = [
  { label: "Right", value: "R" },
  { label: "Left", value: "L" },
];

/** ------------------ COMPONENT ------------------ */
export default function PlanDrawer({ open, onClose, onSubmit }) {
  const [form] = Form.useForm();
  const [committed, setCommitted] = useState([]); // items added via the + button
  const [editIndex, setEditIndex] = useState(null);

  // ---- helpers to label things ----
  const findOptionLabel = (sectionId, code) => {
    const section = SECTION_BY_ID[sectionId];
    return section?.options?.find(o => o.code === code)?.label || code;
  };
  const findVariantLabel = (sectionId, code, variantCode) => {
    const opt = SECTION_BY_ID[sectionId]?.options?.find(o => o.code === code);
    return opt?.variants?.find(v => v.code === variantCode)?.label || variantCode;
  };

  // Normalize a single row into the payload shape
const normalizeRow = (row) => {
  const section = SECTION_BY_ID[row.sectionId];
  if (!section) return null;

  if (row.sectionId === 7) {
    const choices = row.smdChoices || [];
    if (!choices.length) return null;
    return { sectionId: 7, sectionLabel: section.label, choices };
  }

  const opts = row.opts || {};
  const choices = [];

  (section.options || []).forEach((opt) => {
    const state = opts[opt.code];
    if (!state) return;

    // L/R options: include only if at least one side is chosen
    if (opt.requiresSides) {
      if (Array.isArray(state.sides) && state.sides.length > 0) {
        choices.push({ code: opt.code, sides: state.sides });
      }
      return;
    }

    // --- Severity-only options: selected iff a severity is set ---
    if (opt.severityOptions) {
      if (state.severity) {
        choices.push({ code: opt.code, severity: state.severity });
      }
      return;
    }

    // Variants/simple: include if selected (variants kept as-is)
    if (state.selected) {
      const entry = { code: opt.code };
      if (opt.variants && Array.isArray(state.variants) && state.variants.length) {
        entry.variants = state.variants;
      }
      choices.push(entry);
    }
  });

  if (!choices.length) return null;
  return { sectionId: section.id, sectionLabel: section.label, choices };
};

  // Turn a normalized item into a short text for the title
  const itemToText = (item) => {
    if (!item) return "";
    const parts = item.choices.map((c) => {
      if (typeof c === "string") {
        return findOptionLabel(item.sectionId, c);
      }
      let t = findOptionLabel(item.sectionId, c.code);
      if (c.sides?.length) t += ` (${c.sides.join("/")})`;
      if (c.severity) t += ` · ${c.severity}`;
      if (c.variants?.length) {
        const vs = c.variants.map(v => findVariantLabel(item.sectionId, c.code, v)).join("+");
        t += ` · ${vs}`;
      }
      return t;
    });
    return <span><span style={{ color: "#006d77" }}>{item.sectionLabel}:</span> {parts.join("; ")}</span>;
  };

  const hydrateRowFromItem = (item) => {
    const row = { sectionId: item.sectionId, smdChoices: [], opts: {} };

    if (item.sectionId === 7) {
      row.smdChoices = item.choices || [];
    } else {
      const opts = {};
      (item.choices || []).forEach((c) => {
        // For requiresSides we only hydrate sides (no selected flag needed)
        if (SECTION_BY_ID[item.sectionId].options.find(o => o.code === c.code)?.requiresSides) {
          opts[c.code] = { sides: c.sides || [] };
        } else {
          opts[c.code] = { selected: true };
          if (c.sides) opts[c.code].sides = c.sides;
          if (c.variants) opts[c.code].variants = c.variants;
          if (c.severity) opts[c.code].severity = c.severity;
        }
      });
      row.opts = opts;
    }

    form.setFields([
      { name: ["items", 0, "sectionId"], value: row.sectionId },
      { name: ["items", 0, "smdChoices"], value: row.smdChoices },
      { name: ["items", 0, "opts"], value: row.opts },
    ]);
  };

  const startEdit = (idx) => {
    setEditIndex(idx);
    hydrateRowFromItem(committed[idx]);
  };

  // -------- “defaults” on section change → only CLEAR now (no auto select) ----------
  const applyDefaultsForSection = (field, _sectionId) => {
    // just clear — blocks that need L/R will be open by design
    form.setFields([
      { name: ["items", field.name, "smdChoices"], value: [] },
      { name: ["items", field.name, "opts"], value: {} },
    ]);
  };

  // + button handler: commit current, then reset row
  const handleCommit = (field) => {
    const row = form.getFieldValue(["items", field.name]);
    if (!row?.sectionId) return message.warning("Pick a section first.");
    const normalized = normalizeRow(row);
    if (!normalized) return message.warning("Select at least one option.");

    setCommitted((prev) => {
      if (editIndex !== null) {
        const copy = [...prev];
        copy[editIndex] = normalized; // update existing
        return copy;
      }
      return [...prev, normalized]; // add new
    });

    setEditIndex(null); // exit edit mode

    // clear the row
    form.setFields([
      { name: ["items", field.name, "sectionId"], value: undefined },
      { name: ["items", field.name, "smdChoices"], value: [] },
      { name: ["items", field.name, "opts"], value: {} },
    ]);
  };

  const handleFinish = (values) => {
      const { items: rows = [], successRate, companion } = values;

    const current = rows.map(normalizeRow).filter(Boolean);
    const payloadItems = [...committed, ...current];

 if (!payloadItems.length) {
      message.warning("No selections to save.");
      return;
    }




  onSubmit?.({
     successRate,
     companion,
     items: payloadItems,
   });
    message.success("Saved");
    setCommitted([]);
    onClose?.();
    form.resetFields();
  };

  // ------------- UI builders -------------

  // Severity as two checkboxes (mutually exclusive, both can be off)
  const SeverityCheckboxes = ({ path }) => {
    const severity = form.getFieldValue(path);
    const toggle = (value) => {
      form.setFields([{ name: path, value: severity === value ? undefined : value }]);
    };
    return (
      <div style={{ marginTop: 8 }}>
        <div style={{ marginBottom: 4, fontWeight: 500 }}>Severity</div>
        <Space>
          <Checkbox checked={severity === "partial"} onChange={() => toggle("partial")}>
            Partial
          </Checkbox>
          <Checkbox checked={severity === "complete"} onChange={() => toggle("complete")}>
            Complete
          </Checkbox>
        </Space>
      </div>
    );
  };

// Use a relative path for Form.Item names, absolute only for get/set.
const renderOptionBlock = (field, opt) => {
  const rel = [field.name, "opts", opt.code];                 // for Form.Item name
  const abs = ["items", field.name, "opts", opt.code];        // for form.getFieldValue paths

  // L/R options: sides picker only (no master checkbox)
  if (opt.requiresSides) {
    return (
      <Card key={opt.code} size="small" style={{ marginTop: 8 }}>
        <div style={{ fontWeight: 500 }}>{opt.label}</div>
        <Form.Item name={[...rel, "sides"]} style={{ marginTop: 8, marginBottom: 0 }}>
          <Checkbox.Group options={SIDE_OPTIONS} />
        </Form.Item>
      </Card>
    );
  }

  // Severity-only options: no master checkbox
  if (opt.severityOptions) {
    return (
      <Card key={opt.code} size="small" style={{ marginTop: 8 }}>
        <div style={{ fontWeight: 500 }}>{opt.label}</div>
        <SeverityCheckboxes path={[...abs, "severity"]} />
      </Card>
    );
  }

  // Others keep the toggle
  return (
    <Card key={opt.code} size="small" style={{ marginTop: 8 }}>
      <Form.Item name={[...rel, "selected"]} valuePropName="checked" style={{ marginBottom: 0 }}>
        <Checkbox>{opt.label}</Checkbox>
      </Form.Item>

      <Form.Item noStyle shouldUpdate>
        {() => {
          const selected = form.getFieldValue([...abs, "selected"]);
          if (!selected && !opt.variants) return null;
          return (
            <>
              {opt.variants && (
                <Form.Item name={[...rel, "variants"]} label="Variants" style={{ marginTop: 8, marginBottom: 0 }}>
                  <Checkbox.Group options={opt.variants.map(v => ({ value: v.code, label: v.label }))} />
                </Form.Item>
              )}
            </>
          );
        }}
      </Form.Item>
    </Card>
  );
};


  const renderDetails = (field) => {
    const sectionId = form.getFieldValue(["items", field.name, "sectionId"]);
    if (!sectionId) return null;

    const cfg = SECTION_BY_ID[sectionId];
    if (!cfg) return null;

    if (sectionId === 7) {
      return (
        <Form.Item name={[field.name, "smdChoices"]} label="S.M.D choices">
          <Checkbox.Group options={(cfg.options || []).map(o => ({ label: o.label, value: o.code }))} />
        </Form.Item>
      );
    }

    return (
      <>
        <Title level={5} style={{ marginTop: 0 }}>{cfg.label}</Title>

        {/* simple toggles (no sides/variants/severity) */}
        <Row gutter={[12, 0]}>
          {(cfg.options || [])
            .filter(o => !o.requiresSides && !o.variants && !o.severityOptions)
            .map(o => (
              <Col xs={24} sm={12} key={o.code}>
                <Form.Item name={[field.name, "opts", o.code, "selected"]} valuePropName="checked">
                  <Checkbox>{o.label}</Checkbox>
                </Form.Item>
              </Col>
            ))}
        </Row>

        {/* complex ones (L/R, variants, severity) */}
        {(cfg.options || [])
          .filter(o => o.requiresSides || o.variants || o.severityOptions)
          .map(o => renderOptionBlock(field, o))}
      </>
    );
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Add items"
      width={720}
      extra={
        <Space>
          <Button onClick={() => form.submit()} type="primary">Save</Button>
        </Space>
      }
    >
   

      <Form form={form} layout="vertical" onFinish={handleFinish}>

        <Row gutter={12}>
  <Col xs={24} sm={8}>
    <Form.Item
      name="successRate"
      label="Success rate (%)"
      rules={[{ type: "number", min: 0, max: 100 }]}
    >
      <InputNumber min={0} max={100} addonAfter="%" style={{ width: "100%" }} />
    </Form.Item>
  </Col>

  <Col xs={24} sm={16}>
    <Form.Item
      name="companion"
      label="The patient's companion"
    >
      <Input placeholder="Name" />
    </Form.Item>
  </Col>
</Row>

        <Form.List name="items" initialValue={[{}]}>
          {(fields, { remove }) => (
            <>
              {fields.map((field) => (
                <Card
                  key={field.key}
                  hoverable
                  style={{ marginBottom: 12 }}
                  title={<span>Item</span>}
                  extra={
                    <Space>
                      <Button
                        type="text"
                        icon={<PlusOutlined />}
                        onClick={() => handleCommit(field)}
                        title="Add this selection"
                      />
                      {fields.length > 1 && (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                          title="Remove row"
                        />
                      )}
                    </Space>
                  }
                >
                  {/* Section picker */}
                  <Form.Item
                    name={[field.name, "sectionId"]}
                    label="Section"
                    rules={[{ required: true, message: "Pick a section" }]}
                  >
                    <Select
                      placeholder="Choose a section"
                      options={SECTION_OPTIONS.filter(
                        o => !committed.some(c => c.sectionId === o.value) ||
                             (editIndex !== null && committed[editIndex]?.sectionId === o.value)
                      )}
                      optionFilterProp="label"
                      onChange={(val) => {
                        form.setFields([{ name: ["items", field.name, "sectionId"], value: val }]);
                        applyDefaultsForSection(field, val);
                      }}
                    />
                  </Form.Item>

                  {/* Details area */}
                  <Form.Item noStyle shouldUpdate>
                    {() => renderDetails(field)}
                  </Form.Item>
                </Card>
              ))}
            </>
          )}
        </Form.List>
      </Form>


         {committed.length > 0 && (
       <Card size="default" style={{ marginBottom: 12 }}>
  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    {committed.map((it, idx) => (
      <Row key={idx}
        style={{ border: "1px solid #006d77", borderRadius: 6, padding: "2px 8px" }}>
                <Col span={24}>
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => startEdit(idx)}
                    title="Edit"
                  >
                    چاکردن
                  </Button>
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => setCommitted(prev => prev.filter((_, i) => i !== idx))}
                    title="Remove"
                  >
                    سڕینەوە
                  </Button>
                </Col>
                <Col style={{ textAlign: "left" }} span={24}>
                  <span style={{ display: 'block' }}>{itemToText(it)}</span>
                </Col>
              </Row>
            ))}
          </div>
        </Card>
      )}
    </Drawer>
  );
}
